import { useState, useEffect, useContext, useMemo } from "react"
import { NavLink } from "react-router-dom"
import { AuthContext } from "../../../../utils/providers/auth"
import { AlertsContext } from "../../../../utils/providers/alerts"
import { db } from "../../../../utils/firebase"
import firebase from "firebase"
import moment from "moment"

// UI imports
import Table from "../../../../components/structure/table/table"
import Badge from "../../../../components/ui/badge/badge"
import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Button from "../../../../components/ui/button/button"
import Window from "../../../../components/structure/window/window"

import { CONTRACT_NIGHTS, TOTAL_CONTRACTED_ROOM_NIGHTS } from "./hotel-contracts"
import { buildHotelOccupancy, summariseRows, getRoomNightsInBlock, getFillPercentage, getFillBadgeType, formatShortNightLabel } from "./hotel-occupancy"

// Days of the week for the selector
const DAYS = {
	monday: "Monday",
	tuesday: "Tuesday",
	wednesday: "Wednesday",
	thursday: "Thursday",
	friday: "Friday",
	saturday: "Saturday",
	sunday: "Sunday",
}

// A single contracted property, summarised for the strip along the top.
function ContractCard({ row }) {
	const { contract } = row
	const used = getRoomNightsInBlock(row)
	const fill = getFillPercentage(row)

	return (
		<div
			style={{
				flex: "0 0 auto",
				border: "1px solid #e6e6e6",
				borderRadius: "6px",
				padding: "14px 16px",
			}}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "10px" }}>
				<strong style={{ fontSize: "15px" }}>{contract.contract_name}</strong>
				<Badge
					type={getFillBadgeType(fill)}
					label={`${fill}%`}
				/>
			</div>

			<div style={{ fontSize: "17px", fontWeight: 600, lineHeight: 1.2, marginBottom: "10px" }}>
				{used} <span style={{ fontWeight: 400, color: "#8c8c8c" }}>/ {contract.room_nights}</span>
			</div>

			{(contract.room_types || []).map((roomType) => (
				<div
					key={roomType.label}
					style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12px", color: "#8c8c8c", lineHeight: 1.6 }}>
					<span>{roomType.label}</span>
					<span style={{ fontWeight: 500 }}>{roomType.peak_rooms}</span>
				</div>
			))}
		</div>
	)
}

// Explains what the numbers in the grid below mean.
function GridKey() {
	const swatch = (colour) => ({
		width: "11px",
		height: "11px",
		borderRadius: "3px",
		background: colour,
		flex: "0 0 auto",
	})

	return (
		<div style={{ display: "flex", alignItems: "center", gap: "16px", fontSize: "12px", color: "#8c8c8c" }}>
			<span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
				<span style={swatch("#1c1c1c")} />
				Within the block
			</span>
			<span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
				<span style={swatch("#c0392b")} />
				Over the rooms we hold
			</span>
		</div>
	)
}

// Returns the hotels table for the Masters 2027 page
export default function Hotels({ bookings, guests, tickets, eventId }) {
	const [searchTerm, setSearchTerm] = useState("")
	const [selectedDay, setSelectedDay] = useState("")
	const [filteredGuests, setFilteredGuests] = useState(guests)
	const [showAssignModal, setShowAssignModal] = useState(false)
	const [selectedGuest, setSelectedGuest] = useState(null)
	const [ticketCode, setTicketCode] = useState("")
	const [assigningTicket, setAssigningTicket] = useState(false)
	const [selectedTicketDay, setSelectedTicketDay] = useState("")

	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)

	// Room nights are a property of the booking's stay rather than of an
	// individual guest, so this is built from the bookings, not the guest list.
	const occupancy = useMemo(() => buildHotelOccupancy(bookings), [bookings])

	// Only the properties we hold a block at - anything else is not exposure.
	const contractedRows = useMemo(() => occupancy.rows.filter((row) => row.contract), [occupancy])
	const totals = useMemo(() => summariseRows(contractedRows, occupancy.guestCountByBooking), [contractedRows, occupancy])

	// Any night someone is actually in house that falls outside the contracted
	// window, so the table still reconciles to the totals.
	const extraNights = useMemo(() => {
		return Object.keys(totals.nights)
			.filter((date) => !CONTRACT_NIGHTS.includes(date))
			.sort()
	}, [totals])

	const totalUsedInBlock = totals.roomNights - totals.roomNightsOutsideBlock

	// When the search term or selected day changes, filter the guests
	useEffect(() => {
		// If both search term and selected day are empty, show all guests
		if (!searchTerm && !selectedDay) {
			setFilteredGuests(guests)
			return
		}

		// Filter the guests
		const filtered = guests.filter((guest) => {
			const fullName = `${guest.first_name || ""} ${guest.last_name || ""}`.toLowerCase()
			const reference = String(guest.booking_reference || "").toLowerCase()
			const hotelNames = (guest.hotel_names || []).join(", ").toLowerCase()

			// Check if the guest matches the search term
			const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || reference.includes(searchTerm.toLowerCase()) || hotelNames.includes(searchTerm.toLowerCase())

			// Check if the guest has the selected day in their masters days
			const matchesDay = !selectedDay || (guest.masters_days && guest.masters_days[selectedDay])

			return matchesSearch && matchesDay
		})

		// Update the state for the table
		setFilteredGuests(filtered)
	}, [searchTerm, selectedDay, guests])

	// Assign ticket to guest
	const assignTicket = async () => {
		if (!eventId) {
			pushAlert({
				type: "ERROR",
				title: "Error",
				body: "Missing eventId for ticket assignment",
			})
			return
		}
		if (!selectedGuest || !ticketCode) {
			pushAlert({
				type: "ERROR",
				title: "Error",
				body: "Please enter a ticket code",
			})
			return
		}

		try {
			setAssigningTicket(true)

			// Check if guest already has an active ticket
			const guestActiveTickets = tickets.filter((t) => t.guestID === selectedGuest.guestID && !t.returned)
			if (guestActiveTickets.length > 0) {
				pushAlert({
					type: "ERROR",
					title: "Error",
					body: "This guest already has an active ticket",
				})
				return
			}

			// Check if ticket code exists and its status
			const existingTicket = tickets.find((t) => t.code === ticketCode.toUpperCase())
			if (existingTicket && !existingTicket.returned) {
				pushAlert({
					type: "ERROR",
					title: "Error",
					body: "This ticket is already assigned and not returned",
				})
				return
			}

			// Create new ticket record (write both legacy + normalized field names)
			const day = selectedTicketDay || moment().format("dddd").toLowerCase()
			const newTicket = {
				code: ticketCode.toUpperCase(),
				guestID: selectedGuest.guestID,
				bookingID: selectedGuest.bookingID,
				assigned_by: user.uid,
				assigned_at: firebase.firestore.FieldValue.serverTimestamp(),
				// legacy
				assignedBy: user.uid,
				assignedAt: firebase.firestore.FieldValue.serverTimestamp(),
				returned: false,
				returned_at: null,
				// legacy
				returnedAt: null,
				picked_up: false,
				day,
			}

			// Add to tickets collection
			const ticketRef = await db.collection(`events/${eventId}/tickets`).add(newTicket)

			// Create log entry for ticket assignment
			await db.collection(`events/${eventId}/logs`).add({
				action: "ticket_assigned",
				ticketID: ticketRef.id,
				ticketCode: ticketCode.toUpperCase(),
				guestID: selectedGuest.guestID,
				bookingID: selectedGuest.bookingID,
				assigned_by: user.uid,
				timestamp: firebase.firestore.FieldValue.serverTimestamp(),
				details: {
					guestName: `${selectedGuest.first_name || ""} ${selectedGuest.last_name || ""}`.trim(),
					reference: selectedGuest.booking_reference,
					day,
				},
			})

			// Clear form and close modal
			setTicketCode("")
			setShowAssignModal(false)
			setSelectedGuest(null)
			setSelectedTicketDay("")

			pushAlert({
				type: "SUCCESS",
				title: "Success",
				body: "Ticket assigned successfully",
			})
		} catch (error) {
			console.error("Error assigning ticket:", error)
			pushAlert({
				type: "ERROR",
				title: "Error",
				body: "Failed to assign ticket",
			})
		} finally {
			setAssigningTicket(false)
		}
	}

	const nightColumns = [...CONTRACT_NIGHTS, ...extraNights]

	return (
		<>
			{/* Contracted room block exposure, hard-coded from the signed agreements */}
			<div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px" }}>
				{contractedRows.map((row) => (
					<ContractCard
						key={row.resortID}
						row={row}
					/>
				))}
			</div>

			{/* Rooms in house each night against the contracted block */}
			<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
				<GridKey />
			</div>

			<Table
				className="bookings-table"
				headings={[
					"Hotel",
					...nightColumns.map((date) => formatShortNightLabel(date)),
					"Total",
				]}>
				{contractedRows.map((row) => {
					const fill = getFillPercentage(row)

					return (
						<tr key={row.resortID}>
							<td>{row.name}</td>

							{nightColumns.map((date) => {
								const night = row.nights[date]
								const contracted = row.contract?.nightly_block?.[date] || 0

								if (!night && !contracted) {
									return <td key={date}>-</td>
								}

								const over = contracted > 0 && night?.rooms > contracted

								return (
									<td
										key={date}
										style={{ verticalAlign: "middle" }}>
										<div style={{ lineHeight: 1.25 }}>
											<span style={{ fontWeight: 500, color: over ? "#c0392b" : "inherit" }}>
												{night?.rooms || 0}
												{contracted > 0 ? <span style={{ fontWeight: 400, color: "#8c8c8c" }}> / {contracted}</span> : null}
											</span>
										</div>
										<div style={{ lineHeight: 1.25, fontSize: "12px", color: "#8c8c8c" }}>{night?.guests || 0} guests</div>
									</td>
								)
							})}

							<td style={{ verticalAlign: "middle" }}>
								<div style={{ lineHeight: 1.25 }}>
									<span style={{ fontWeight: 600 }}>
										{getRoomNightsInBlock(row)}
										<span style={{ fontWeight: 400, color: "#8c8c8c" }}> / {row.contract.room_nights}</span>
									</span>{" "}
									<Badge
										type={getFillBadgeType(fill)}
										label={`${fill}%`}
									/>
								</div>
								<div style={{ lineHeight: 1.25, fontSize: "12px", color: "#8c8c8c" }}>
									{row.guests} guests
									{row.roomNightsOutsideBlock > 0 ? ` - ${row.roomNightsOutsideBlock} night${row.roomNightsOutsideBlock === 1 ? "" : "s"} outside the block` : ""}
								</div>
							</td>
						</tr>
					)
				})}

				<tr style={{ fontWeight: 600 }}>
					<td>
						<strong>Total</strong>
					</td>

					{nightColumns.map((date) => {
						const night = totals.nights[date]

						return (
							<td
								key={date}
								style={{ verticalAlign: "middle" }}>
								<div style={{ lineHeight: 1.25 }}>{night?.rooms || 0}</div>
								<div style={{ lineHeight: 1.25, fontSize: "12px", color: "#8c8c8c", fontWeight: 400 }}>{night?.guests || 0} guests</div>
							</td>
						)
					})}

					<td style={{ verticalAlign: "middle" }}>
						<div style={{ lineHeight: 1.25 }}>
							{totalUsedInBlock}
							<span style={{ fontWeight: 400, color: "#8c8c8c" }}> / {TOTAL_CONTRACTED_ROOM_NIGHTS}</span>
						</div>
						<div style={{ lineHeight: 1.25, fontSize: "12px", color: "#8c8c8c", fontWeight: 400 }}>
							{totals.guests} guests
							{totals.roomNightsOutsideBlock > 0 ? ` - ${totals.roomNightsOutsideBlock} night${totals.roomNightsOutsideBlock === 1 ? "" : "s"} outside the block` : ""}
						</div>
					</td>
				</tr>
			</Table>

			<br />
			<br />

			<div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
				<div style={{ width: "400px" }}>
					<Input
						value={searchTerm}
						onChange={(value) => setSearchTerm(value)}
						placeholder="Search by hotel, client name or booking reference..."
						type="text"
					/>
				</div>
				<div style={{ width: "200px" }}>
					<Select
						selected={selectedDay}
						placeholder="Select a day"
						options={DAYS}
						onSelect={({ option }) => setSelectedDay(option)}
					/>
				</div>
				<div>
					<Button
						label="Clear"
						type="SECONDARY"
						onClick={() => {
							setSearchTerm("")
							setSelectedDay("")
						}}
					/>
				</div>
			</div>

			<Table
				className="bookings-table"
				headings={["Booking", "Guest Name", "Hotel", "Masters Days", "Tickets", ""]}>
				{filteredGuests.length > 0 ? (
					[...filteredGuests]
						.sort((a, b) => {
							// First sort by hotel name
							const hotelA = a.hotel_names[0] || ""
							const hotelB = b.hotel_names[0] || ""
							const hotelCompare = hotelA.localeCompare(hotelB)
							if (hotelCompare !== 0) return hotelCompare

							// Then by last name
							return (a.last_name || "").localeCompare(b.last_name || "")
						})
						.map((person) => {
							// Get all the tickets on this guest
							const personTickets = tickets?.filter((ticket) => ticket.guestID === person.guestID)

							return (
								<tr key={person.guestID}>
									<td>
										<NavLink to={`/booking/${person.bookingID}`}>{person.booking_reference !== "-" ? person.booking_reference : "Unknown"}</NavLink>
									</td>
									<td>{`${person.first_name || ""} ${person.last_name || ""}`.trim() || "-"}</td>
									<td>{person.hotel_names[0] || "-"}</td>
									<td>{person.masters_days_string || "-"}</td>
									<td>
										{personTickets.length > 0 ? (
											<div style={{ display: "flex", gap: "12px" }}>
												{personTickets.map((ticket) => (
													<div key={ticket.id}>
														{ticket.code} ({ticket.day.charAt(0).toUpperCase() + ticket.day.slice(1, 3)}){" "}
														<Badge
															type={ticket.returned ? "POSITIVE" : "AMBER"}
															label={ticket.returned ? "Returned" : "Not Returned"}
														/>
													</div>
												))}
											</div>
										) : (
											"-"
										)}
									</td>
									<td>
										<Button
											label="Assign Ticket"
											small
											onClick={() => {
												setSelectedGuest(person)
												setShowAssignModal(true)
											}}
										/>
									</td>
								</tr>
							)
						})
				) : (
					<tr>
						<td
							colSpan="6"
							className="masters-no-data">
							No guests found
						</td>
					</tr>
				)}
			</Table>

			{showAssignModal && (
				<Window
					title="Assign Ticket"
					className="slim"
					close={() => {
						setShowAssignModal(false)
						setSelectedGuest(null)
						setTicketCode("")
						setSelectedTicketDay("")
					}}>
					<div style={{ marginBottom: "20px" }}>
						<h3 style={{ marginBottom: "8px", fontWeight: 500 }}>Guest Details</h3>
						<p style={{ marginBottom: "4px", fontSize: "15px" }}>Name: {`${selectedGuest.first_name || ""} ${selectedGuest.last_name || ""}`.trim()}</p>
						<p style={{ marginBottom: "4px", fontSize: "15px" }}>Booking: {selectedGuest.booking_reference}</p>
						<p style={{ marginBottom: "4px", fontSize: "15px" }}>Hotel: {selectedGuest.hotel_names[0]}</p>
					</div>

					<div style={{ marginBottom: "12px" }}>
						<Input
							value={ticketCode}
							onChange={(value) => setTicketCode(value.toUpperCase())}
							placeholder="Enter ticket code..."
							type="text"
						/>
					</div>

					<div style={{ marginBottom: "12px" }}>
						<Select
							selected={selectedTicketDay}
							placeholder={`Today (${moment().format("dddd")})`}
							options={DAYS}
							onSelect={({ option }) => setSelectedTicketDay(option)}
						/>
					</div>

					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<Button
							label="Assign Ticket"
							loading={assigningTicket}
							onClick={assignTicket}
						/>
					</div>
				</Window>
			)}
		</>
	)
}
